import { Product, Store } from "../../../../types";
import { useState } from "react";
import { getProductReport, getProductReportCsv } from "../../../../lib/admin";
import { useReportPage } from "../hooks/useReportPage";
import { useHasScope } from "../../../../utils/Permission";
import StoreReportLoadingRows from '../shared/StoreReportLoadingRows';
import StoreReportRow from '../shared/StoreReportRow';
import StoreReportRowHeader from '../shared/StoreReportRowHeader';
import StoreReportProductDetails from './StoreReportProductDetails';
import StoreReportToolbar from '../StoreReportToolbar';
import { EntityLabel } from '../ForceSyncSelectedButton';
import StoreReportNoResultsMessage from '../StoreReportNoResultsMessage';
import Notification from '../../../global/Notification';
import Pagination from '../../../global/Pagination';
import { saveAs } from 'file-saver';

const PRODUCTS_PER_PAGE = 25;
const MAX_SELECTED_PRODUCTS = 100;
const ENTITY_LABEL: EntityLabel = { one: "product", many: "products" };

type Props =
    {
        store: Store
        onForceSyncAll?: () => Promise<string>
        onForceSyncSelection?: (entityIds: number[]) => Promise<string>
        isSyncing?: boolean
    }

export default function StoreReportProductList({ store, onForceSyncAll, onForceSyncSelection, isSyncing }: Props) {
    const canSeeDetails = useHasScope('reports:view-details');
    const [isDownloading, setDownloading] = useState<boolean>(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const report = useReportPage<Product>({
        storeId: store.id,
        pageSize: PRODUCTS_PER_PAGE,
        maxSelected: MAX_SELECTED_PRODUCTS,
        selectionLimitNotice: `You can select up to ${MAX_SELECTED_PRODUCTS} ${ENTITY_LABEL.many} at a time.`,
        fetchPage: async (page, pageSize, search, status) => {
            const result = await getProductReport(store.id, page, pageSize, search, status);
            return { rows: result.products ?? [], total: result.pagination?.total ?? 0 };
        },
        isSyncing,
        onForceSyncSelection,
    });

    const openInShopify = (shopifyId: string) => {
        const storeSplit = store.storeUrl.split('.');
        const storeURL = storeSplit[0];
        window.open(`https://admin.shopify.com/store/${storeURL}/products/${shopifyId}`, '_blank');
    }

    const generateCSV = async () => {
        setDownloading(true);
        setDownloadError(null);
        try {
            const csv = await getProductReportCsv(store.id, report.search, report.status, canSeeDetails);

            saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), `products-${store.id}.csv`);
        }
        catch {
            setDownloadError("Unable to download the report. Please try again later.");
        }
        finally {
            setDownloading(false);
        }
    };

    return (
        <div className="products products-report">
            <StoreReportToolbar
                search={report.search}
                setSearch={report.updateSearch}
                status={report.status}
                setStatus={report.updateStatus}
                onRefresh={report.refresh}
                downloadCSV={generateCSV}
                isDownloading={isDownloading}
                isLoading={report.isLoading}
                onForceSyncAll={onForceSyncAll}
                onForceSyncSelected={onForceSyncSelection && report.forceSyncSelection}
                selectedCount={report.selectedIds.length}
                entityLabel={ENTITY_LABEL}
                onClearSelection={report.clearSelection}
                isSyncing={isSyncing}
            />
            {!report.retrieveError && <StoreReportRowHeader allSelected={report.allOnPageSelected} onToggleAll={report.toggleAllOnPage} />}
            {report.isLoading ? (
                <StoreReportLoadingRows />
            ) : report.retrieveError ? (
                <p className="sync-error-retrieve">Currently unable to retrieve the information. Please try again later.</p>
            ) : report.rows.length === 0 ? (
                report.search || report.status ? (
                    <StoreReportNoResultsMessage
                        reportName="Products"
                        searchTerm={report.search}
                        onClear={report.clearFilters}
                    />
                ) : (
                    <p className="no-products-message">No products available to display</p>
                )
            ) : (
                <div>
                    {report.rows.map((product) => (
                        <div key={product.id} className="products-list-view">
                            <StoreReportRow
                                row={product}
                                openInShopify={openInShopify}
                                selected={report.selectedIds.includes(product.id)}
                                onToggleSelected={report.toggleSelected}
                                isExpanded={report.expandedId === product.id}
                                onToggleDetails={report.toggleDetails}
                                renderDetails={() => <StoreReportProductDetails storeId={store.id} product={product} />}
                            ></StoreReportRow>
                        </div>
                    ))}

                    <Pagination
                        pageSize={PRODUCTS_PER_PAGE}
                        totalItems={report.total}
                        paginate={report.setCurrentPage}
                        currentPage={report.currentPage}
                    />
                </div>
            )}
            <Notification message={downloadError} type="error" onClose={() => setDownloadError(null)} />
            <Notification message={report.selectionNotice} type="error" onClose={report.dismissNotice} />
        </div>
    );
}